import { inject, provide, reactive, ref } from 'vue';
import type { InjectionKey, Ref } from 'vue';

/**
 * Floating panel system (redesign F2): panels live on the Z axis above the
 * canvas — draggable, several open at once, positions persisted. Only the
 * full builder provides a manager; Canvas-lite keeps the docked layout and
 * consumers degrade on null.
 */
/** Photoshop-style dock edges a float can snap into; null = floating. */
export type DockEdge = 'left' | 'right' | 'bottom';

export interface PanelState {
    x: number;
    y: number;
    open: boolean;
    /** Explicit size after a user resize; undefined = component default width / auto height. */
    width?: number;
    height?: number;
    /** Docked edge, or undefined/null when floating. */
    dock?: DockEdge | null;
}

/** Floors keep a panel usable; ceilings track the viewport. */
export const PANEL_MIN_WIDTH = 220;
export const PANEL_MIN_HEIGHT = 160;

/** Dock wells own their size — panels inside just fill them. */
const DOCK_DEFAULT_SIZES: Record<DockEdge, number> = { left: 300, right: 300, bottom: 240 };

export interface PanelManager {
    isOpen(id: string): boolean;
    open(id: string): void;
    close(id: string): void;
    toggle(id: string): void;
    /** Closes the top-most open panel; false when nothing was open. */
    closeTop(): boolean;
    bringToFront(id: string): void;
    positionOf(id: string): { x: number; y: number };
    setPosition(id: string, x: number, y: number): void;
    /** null = no explicit override; consumer falls back to its default width / auto height. */
    sizeOf(id: string): { width: number | null; height: number | null };
    setSize(id: string, width: number, height: number): void;
    dockOf(id: string): DockEdge | null;
    setDock(id: string, edge: DockEdge | null): void;
    /** True when at least one open panel sits in the edge's dock well. */
    hasDocked(edge: DockEdge): boolean;
    /** Well size in px — width for side docks, height for the bottom dock. */
    dockSizeOf(edge: DockEdge): number;
    setDockSize(edge: DockEdge, size: number): void;
    /** Edge the pointer is hovering during a drag (drives the ghost preview). */
    dockPreview: Ref<DockEdge | null>;
    setDockPreview(edge: DockEdge | null): void;
    zOf(id: string): number;
}

const PANELS_KEY: InjectionKey<PanelManager> = Symbol('builder-v2-panels');

/** Panels never leave the viewport entirely — keep a grabbable strip. */
const EDGE_MARGIN = 48;

interface PanelDefault {
    x: number;
    y: number;
    open: boolean;
}

export type PanelDefaults = Record<string, PanelDefault>;

function loadStored(storageKey: string): Record<string, Partial<PanelState>> {
    try {
        const raw = window.localStorage.getItem(storageKey);

        return raw ? (JSON.parse(raw) as Record<string, Partial<PanelState>>) : {};
    } catch {
        return {};
    }
}

function clampToViewport(x: number, y: number): { x: number; y: number } {
    const maxX = Math.max(0, window.innerWidth - EDGE_MARGIN);
    const maxY = Math.max(0, window.innerHeight - EDGE_MARGIN);

    return {
        x: Math.min(Math.max(x, -240), maxX),
        y: Math.min(Math.max(y, 0), maxY),
    };
}

function loadStoredDockSizes(storageKey: string): Record<DockEdge, number> {
    const sizes = { ...DOCK_DEFAULT_SIZES };

    try {
        const raw = window.localStorage.getItem(storageKey);
        const stored = raw ? (JSON.parse(raw) as Partial<Record<DockEdge, unknown>>) : {};

        for (const edge of Object.keys(sizes) as DockEdge[]) {
            if (typeof stored[edge] === 'number') {
                sizes[edge] = stored[edge];
            }
        }
    } catch {
        // storage unavailable/corrupted — defaults apply
    }

    return sizes;
}

export function createPanelManager(defaults: PanelDefaults, options: { storagePrefix?: string } = {}): PanelManager {
    const storageKey = `${options.storagePrefix ?? 'pb2'}:panels:v1`;
    const dockStorageKey = `${options.storagePrefix ?? 'pb2'}:docks:v1`;
    const stored = loadStored(storageKey);
    const panels = reactive<Record<string, PanelState>>({});
    const dockSizes = reactive<Record<DockEdge, number>>(loadStoredDockSizes(dockStorageKey));
    const zIndexes = reactive<Record<string, number>>({});
    const zTop = ref(600);
    const dockPreview = ref<DockEdge | null>(null);
    const openOrder: string[] = [];

    for (const [id, fallback] of Object.entries(defaults)) {
        const saved = stored[id];
        const position = clampToViewport(
            typeof saved?.x === 'number' ? saved.x : fallback.x,
            typeof saved?.y === 'number' ? saved.y : fallback.y,
        );

        panels[id] = {
            ...position,
            open: typeof saved?.open === 'boolean' ? saved.open : fallback.open,
            width: typeof saved?.width === 'number' ? saved.width : undefined,
            height: typeof saved?.height === 'number' ? saved.height : undefined,
            dock: saved?.dock ?? null,
        };
        zIndexes[id] = zTop.value;

        if (panels[id].open) {
            openOrder.push(id);
            zIndexes[id] = ++zTop.value;
        }
    }

    let persistTimer: ReturnType<typeof setTimeout> | null = null;

    /** Trailing debounce — a 120Hz drag would otherwise stringify+write
     *  localStorage on every move and jank the gesture. */
    function persist(): void {
        if (persistTimer !== null) {
            clearTimeout(persistTimer);
        }

        persistTimer = setTimeout(() => {
            persistTimer = null;

            try {
                window.localStorage.setItem(storageKey, JSON.stringify(panels));
                window.localStorage.setItem(dockStorageKey, JSON.stringify(dockSizes));
            } catch {
                // storage unavailable — panels still work, positions just reset
            }
        }, 150);
    }

    function bringToFront(id: string): void {
        zIndexes[id] = ++zTop.value;
        const index = openOrder.indexOf(id);

        if (index > -1) {
            openOrder.splice(index, 1);
        }

        openOrder.push(id);
    }

    const manager: PanelManager = {
        isOpen: (id) => panels[id]?.open === true,
        open(id) {
            if (!panels[id]) {
                return;
            }

            panels[id].open = true;
            bringToFront(id);
            persist();
        },
        close(id) {
            if (!panels[id]) {
                return;
            }

            panels[id].open = false;
            const index = openOrder.indexOf(id);

            if (index > -1) {
                openOrder.splice(index, 1);
            }

            persist();
        },
        toggle(id) {
            if (panels[id]?.open) {
                manager.close(id);
            } else {
                manager.open(id);
            }
        },
        closeTop() {
            const top = openOrder[openOrder.length - 1];

            if (!top) {
                return false;
            }

            manager.close(top);

            return true;
        },
        bringToFront,
        positionOf: (id) => ({ x: panels[id]?.x ?? 16, y: panels[id]?.y ?? 16 }),
        setPosition(id, x, y) {
            if (!panels[id]) {
                return;
            }

            const clamped = clampToViewport(x, y);
            panels[id].x = clamped.x;
            panels[id].y = clamped.y;
            persist();
        },
        sizeOf: (id) => ({ width: panels[id]?.width ?? null, height: panels[id]?.height ?? null }),
        setSize(id, width, height) {
            if (!panels[id]) {
                return;
            }

            const maxWidth = Math.max(PANEL_MIN_WIDTH, window.innerWidth - 32);
            const maxHeight = Math.max(PANEL_MIN_HEIGHT, window.innerHeight - 32);
            panels[id].width = Math.round(Math.min(Math.max(width, PANEL_MIN_WIDTH), maxWidth));
            panels[id].height = Math.round(Math.min(Math.max(height, PANEL_MIN_HEIGHT), maxHeight));
            persist();
        },
        dockOf: (id) => panels[id]?.dock ?? null,
        setDock(id, edge) {
            if (!panels[id]) {
                return;
            }

            panels[id].dock = edge;
            bringToFront(id);
            persist();
        },
        hasDocked: (edge) => Object.values(panels).some((panel) => panel.open && panel.dock === edge),
        dockSizeOf: (edge) => dockSizes[edge],
        setDockSize(edge, size) {
            const min = edge === 'bottom' ? PANEL_MIN_HEIGHT : PANEL_MIN_WIDTH;
            const max = edge === 'bottom'
                ? Math.max(min, Math.round(window.innerHeight * 0.6))
                : Math.max(min, Math.round(window.innerWidth * 0.5));

            dockSizes[edge] = Math.round(Math.min(Math.max(size, min), max));
            persist();
        },
        dockPreview,
        setDockPreview(edge) {
            dockPreview.value = edge;
        },
        zOf: (id) => zIndexes[id] ?? 600,
    };

    provide(PANELS_KEY, manager);

    return manager;
}

/** Null in Canvas-lite — the docked layout has no floating panels. */
export function usePanelManagerOptional(): PanelManager | null {
    return inject(PANELS_KEY, null);
}
